import { Navigate, Route, createRoutesFromElements } from 'react-router';
import {
  addonId,
  billingAccountId,
  caseDisplayId,
  catalogCode,
  contractDisplayId,
  deliveryId,
  deviceName,
  formId,
  historyId,
  invoiceId,
  msisdn,
  orderId,
  page,
  requestId,
  subscriptionId,
  token,
  trainingName,
} from '../../common/constants/pathInterfaces.js';
import { paths } from '../../common/constants/pathVariables.js';
import {
  fetchIdeatArticleLoader,
  fetchIdeatCategoryWithMetaLoader,
  fetchIdeatKeywordsLoader,
  fetchIdeatLandingLoader,
  fetchIdeatTagWithMetaLoader,
  fetchNavigationLoader,
  fetchPublicPageLoader,
  fetchTrainings,
} from '../../common/fetch.js';
import { getLocale, resolveLocaleFromPath } from '../../common/i18n';
import {
  billingAccountInvoiceDocumentsLoader,
  billingAccountInvoicesLoader,
  billingAccountLayoutLoader,
  billingAccountListLoader,
  billingAccountLoader,
  billingAccountSubscriptionsLoader,
  broadbandSubAddOnLoader,
  broadbandSubPostChangeRequestLoader,
  broadbandSubUpdateLoader,
  broadbandSubUpdateM2MLoader,
  broadbandSubscriptionLoader,
  broadbandSubscriptionsLoader,
  catalogConfigurationsLoader,
  catalogProductsLoader,
  catalogSummaryLoader,
  categoryOnlineModelHeadersLoader,
  checkoutRootLoader,
  companyInfoHomeLoader,
  companyInvitesLoader,
  contactLoader,
  contactsLoader,
  createNewBillingAccountLoader,
  customerOrderLoader,
  customerOrdersLoader,
  deliveryOrderLoader,
  deviceAddOnLoader,
  deviceSubPostChangeRequestLoader,
  deviceSubscriptionLoader,
  deviceSubscriptionsLoader,
  dnsRecordHistoryLoader,
  dnsRecordsHistoryLoader,
  dnsSubscriptionsLoader,
  documentsLoader,
  employeeApprovalCustomerOrderLoader,
  eoeDevicePathLoader,
  fetchNavigationGroupsLoader,
  fetchProductPageLoader,
  fixedBBCheckoutLoader,
  fixedBBRootLoader,
  getDnsRecords,
  invitationLandingLoader,
  invitationsLoader,
  invoiceLoader,
  invoicesLoader,
  mfaDetailsLoader,
  mobileIdContractLoader,
  mobileIdContractsLoader,
  mobileSubAddOnLoader,
  mobileSubAttachRingLoader,
  mobileSubAttachVakioLoader,
  mobileSubPostChangeRequestLoader,
  mobileSubUpdateConfirmationLoader,
  mobileSubUpdateLoader,
  moveContactLoader,
  newMobileIdContractLoader,
  notFoundLoader,
  onlineModelsForBasketItemsLoader,
  orderConfirmationsLoader,
  orderMobileBBCommonLoader,
  orderSubscriptionCommonLoader,
  placeOrderLoader,
  publicM2MBroadbandPageLoader,
  punchoutCheckoutLoader,
  reportsLoader,
  serviceSubscriptionLoader,
  serviceSubscriptionsLoader,
  subscriptionActionLoader,
  subscriptionActionsLoader,
  supportCaseFormLoader,
  supportCaseFormPublicLoader,
  supportCaseLoader,
  supportCasesLoader,
  userCustomerOrderLoader,
  userCustomerOrdersLoader,
  vaihtoetuItemGroupsLoader,
  virtualCatalogDetailsLoader,
  virtualCatalogsLoader,
  voiceSubscriptionLoader,
  voiceSubscriptionsLoader,
  warrantyReceiptsLoader,
} from '../../common/loaders.js';
import { categories } from '../../common/utils/categoryUtils.js';
import { EoeCheckout } from '../../components/EoeCheckout/EoeCheckout.js';
import { EoeCheckoutSteps } from '../../components/EoeCheckout/EoeCheckoutSteps.js';
import { EoeCheckoutThankYouOrError } from '../../components/EoeCheckout/EoeCheckoutThankYouOrError.js';
import { EoeOutlet } from '../../components/EoeOutlet/EoeOutlet.js';
import { IdeatArticlePath } from '../../components/IdeatArticlePage/IdeatArticlePage.js';
import {
  IdeatCategoryPath,
  IdeatLandingPath,
  IdeatTagPath,
} from '../../components/IdeatCollectionPage/IdeatCollectionPage.js';
import { IdeatKeywordsPath } from '../../components/IdeatKeywordsPage/IdeatKeywordsPage.js';
import { MfaSelfService } from '../../components/MFA/MfaSelfService';
import { SupportCasePath } from '../../components/MessageCenter/SupportCasePath.js';
import { NewSupportCasePublic } from '../../components/NewSupportCasePublic/NewSupportCasePublic';
import { NewSupportCasePublicThankYou } from '../../components/NewSupportCasePublicThankYou/NewSupportCasePublicThankYou';
import { EoeOmalasku } from '../../components/Omalasku/EoeOmalasku';
import { EoeOmalaskuOrderBarrings } from '../../components/Omalasku/EoeOmalaskuOrderBarrings';
import { EoeOmalaskuSelfService } from '../../components/Omalasku/EoeOmalaskuSelfService';
import { OpenFormLayout } from '../../components/OpenForm/OpenFormLayout/OpenFormLayout.js';
import { OpenFormListLayout } from '../../components/OpenForm/OpenFormList/OpenFormListLayout.js';
import { OpenFormThankYou } from '../../components/OpenForm/OpenFormThankYou/OpenFormThankYou.js';
import { OpenFormViewLayout } from '../../components/OpenForm/OpenFormView/OpenFormViewLayout.js';
import { PlaceOrderCardPayment } from '../../components/PlaceOrder/steps/payment/PlaceOrderCardPayment';
import { PlaceOrderThankYou } from '../../components/PlaceOrder/steps/thankyou/PlaceOrderThankYou';
import { PublicM2MSubscriptions } from '../../components/PublicM2MSubscriptions/PublicM2MSubscriptions';
import { PunchoutHeader } from '../../components/Punchout/partials/PunchoutHeader.js';
import { SiteFn } from './SiteFn.js';
import {
  ExtAuthLayout,
  OmaElisaNavigationLayout,
  OmaElisaProtectedRoutesLayout,
  PlaceOrderLayout,
  PrivateAuthenticationLayout,
  PublicAuthenticationLayout,
  PublicHeaderCmsLayout,
  PublicHeaderLayout,
  PublicPathLayout,
  PunchoutNavigationLayout,
  ShoppingBasketLayout,
} from './SiteLayouts.js';
import { isElisaIdV3Enabled } from './authUtils.js';
import { AnonymousMobileBroadbandPath } from './path/AnonymousMobileBroadbandPath.js';
import { AnonymousMobileVoicePath } from './path/AnonymousMobileVoicePath.js';
import { AuxiliaryEsimOrderPath } from './path/AuxiliaryEsimOrderPath.js';
import { EoeStore } from './path/Employee/DeviceStore/EoeStore.js';
import { EoeStoreProduct } from './path/Employee/DeviceStore/EoeStoreProduct.js';
import { EoeStoreProducts } from './path/Employee/DeviceStore/EoeStoreProducts.js';
import { EoeDevicesPath } from './path/Employee/DevicesAndSubscriptions/plural/EoeDevicesPath.js';
import { EoeSubsPath } from './path/Employee/DevicesAndSubscriptions/plural/EoeSubsPath.js';
import { EoeBroadbandSubPath } from './path/Employee/DevicesAndSubscriptions/singular/EoeBroadbandSubPath.js';
import { EoeDevicePath } from './path/Employee/DevicesAndSubscriptions/singular/EoeDevicePath.js';
import { EoeVoiceSubPath } from './path/Employee/DevicesAndSubscriptions/singular/EoeVoiceSubPath.js';
import { EoeDeviceAddPath } from './path/Employee/DevicesAndSubscriptions/special/EoeDeviceAddPath.js';
import { EoeDeviceSupportRequestPath } from './path/Employee/DevicesAndSubscriptions/special/EoeDeviceSupportRequestPath.js';
import { EoeOneTimePasswordPath } from './path/Employee/DevicesAndSubscriptions/special/EoeOneTimePasswordPath.js';
import { EoeSubAddPath } from './path/Employee/DevicesAndSubscriptions/special/EoeSubAddPath.js';
import { EoeVoiceSubActivateSimPath } from './path/Employee/DevicesAndSubscriptions/special/EoeVoiceSubActivateSimPath.js';
import { EoeVoiceSubOrderSimPath } from './path/Employee/DevicesAndSubscriptions/special/EoeVoiceSubOrderSimPath.js';
import { EoeEmailVerificationPath } from './path/Employee/EoeEmailVerificationPath.js';
import { EoeHomePath } from './path/Employee/EoeHomePath.js';
import { EoeOwnInfoPath } from './path/Employee/EoeOwnInfoPath.js';
import { EoeChangeRequestPath } from './path/Employee/Orders/EoeChangeRequestPath.js';
import { EoeChangeRequestsPath } from './path/Employee/Orders/EoeChangeRequestsPath.js';
import { EoeFinishedReviewItemPath } from './path/Employee/Orders/EoeFinishedReviewItemPath.js';
import { EoeFinishedReviewPath } from './path/Employee/Orders/EoeFinishedReviewPath.js';
import { EoeFinishedReviewsPath } from './path/Employee/Orders/EoeFinishedReviewsPath.js';
import { EoePendingReviewItemPath } from './path/Employee/Orders/EoePendingReviewItemPath.js';
import { EoePendingReviewPath } from './path/Employee/Orders/EoePendingReviewPath.js';
import { EoePendingReviewsPath } from './path/Employee/Orders/EoePendingReviewsPath.js';
import { EoeOwnChangeRequestPath } from './path/Employee/OwnOrders/EoeOwnChangeRequestPath.js';
import { EoeOwnChangeRequestsPath } from './path/Employee/OwnOrders/EoeOwnChangeRequestsPath.js';
import { EoeOwnOrderItemPath } from './path/Employee/OwnOrders/EoeOwnOrderItemPath.js';
import { EoeOwnOrderPath } from './path/Employee/OwnOrders/EoeOwnOrderPath.js';
import { EoeOwnOrdersPath } from './path/Employee/OwnOrders/EoeOwnOrdersPath.js';
import { EoeInvitationAcceptancePath } from './path/EoeInvitationAcceptancePath';
import { EoeSelfRegistrationPath } from './path/EoeSelfRegistrationPath';
import { ErrorPath } from './path/ErrorPath.js';
import { AuthenticationReturnPath } from './path/ExternalAuthentication/AuthenticationReturnPath.js';
import { AuthorizeNumberPortingPath } from './path/ExternalAuthentication/AuthorizeNumberPortingPath.js';
import { AuthorizedNumberPortingPath } from './path/ExternalAuthentication/AuthorizedNumberPortingPath.js';
import { AnonymousFixedBroadbandOrderPath } from './path/FixedBroadbandOrder/AnonymousFixedBroadbandOrderPath.js';
import { FixedBroadbandAvailabilityPath } from './path/FixedBroadbandOrder/FixedBroadbandAvailabilityPath.js';
import { FixedBroadbandCheckoutPath } from './path/FixedBroadbandOrder/FixedBroadbandCheckoutPath.js';
import { FixedBroadbandOffersPath } from './path/FixedBroadbandOrder/FixedBroadbandOffersPath.js';
import { FixedBroadbandThankyouPath } from './path/FixedBroadbandOrder/FixedBroadbandThankyouPath.js';
import { ForgotPasswordPath } from './path/ForgotPasswordPath.js';
import { InvitationLandingPath } from './path/InvitationLandingPath.js';
import { NewSupportCaseAuthenticatedPath } from './path/NewSupportCaseAuthenticatedPath.js';
import { PlaceOrderPath } from './path/PlaceOrderPath.js';
import { InstructionsPath } from './path/Public/InstructionsPath.js';
import { PublicPath } from './path/PublicPath.js';
import { PunchoutCheckoutPath } from './path/Punchout/PunchoutCheckoutPath.js';
import { PunchoutPath } from './path/Punchout/PunchoutPath.js';
import { PunchoutStorePath } from './path/Punchout/PunchoutStorePath.js';
import { PunchoutStoreProduct } from './path/Punchout/PunchoutStoreProduct.js';
import { PunchoutStoreProducts } from './path/Punchout/PunchoutStoreProducts.js';
import { RegistrationPath } from './path/RegistrationPath.js';
import { SearchPublicPagesPath } from './path/SearchPublicPagesPath.js';
import { SelectCompanySignatoryRightPath } from './path/SelectCompanySignatoryRightPath.js';
import { BillingAccountCreateNewPath } from './path/SelfService/Billing/BillingAccountCreateNewPath.js';
import { BillingAccountInvoiceDocumentsPath } from './path/SelfService/Billing/BillingAccountInvoiceDocumentsPath.js';
import { BillingAccountInvoicesPath } from './path/SelfService/Billing/BillingAccountInvoicesPath.js';
import { BillingAccountLayout } from './path/SelfService/Billing/BillingAccountLayout';
import { BillingAccountPath } from './path/SelfService/Billing/BillingAccountPath.js';
import { BillingAccountSubscriptionsPath } from './path/SelfService/Billing/BillingAccountSubscriptionsPath';
import { BillingAccountsPath } from './path/SelfService/Billing/BillingAccountsPath.js';
import { InvoiceDocumentsPath } from './path/SelfService/Billing/InvoiceDocumentsPath.js';
import { InvoicePath } from './path/SelfService/Billing/InvoicePath.js';
import { InvoicesPath } from './path/SelfService/Billing/InvoicesPath.js';
import { CompanyInfoCatalogListEditPath } from './path/SelfService/CompanyInfo/CompanyInfoCatalogListEditPath';
import { CompanyInfoCatalogListInfoPath } from './path/SelfService/CompanyInfo/CompanyInfoCatalogListInfoPath.js';
import { CompanyInfoCatalogListNewPath } from './path/SelfService/CompanyInfo/CompanyInfoCatalogListNewPath.js';
import { CompanyInfoCatalogListProductsPath } from './path/SelfService/CompanyInfo/CompanyInfoCatalogListProductsPath';
import { CompanyInfoCatalogPath } from './path/SelfService/CompanyInfo/CompanyInfoCatalogPath.js';
import {
  CompanyInfoCatalogsOutlet,
  CompanyInfoCatalogsPath,
} from './path/SelfService/CompanyInfo/CompanyInfoCatalogsPath.js';
import { CompanyInfoContactPath } from './path/SelfService/CompanyInfo/CompanyInfoContactPath.js';
import { CompanyInfoContactsPath } from './path/SelfService/CompanyInfo/CompanyInfoContactsPath.js';
import { CompanyInfoHomePath } from './path/SelfService/CompanyInfo/CompanyInfoHomePath.js';
import { CompanyInfoInvitationsPath } from './path/SelfService/CompanyInfo/CompanyInfoInvitationsPath.js';
import { CompanyInfoInvitesPath } from './path/SelfService/CompanyInfo/CompanyInfoInvites.js';
import { CompanyInfoMoveContactPath } from './path/SelfService/CompanyInfo/CompanyInfoMoveContactPath';
import { CompanyInfoMyAccountPath } from './path/SelfService/CompanyInfo/CompanyInfoMyAccountPath.js';
import { CompanyInfoNewContactPath } from './path/SelfService/CompanyInfo/CompanyInfoNewContactPath.js';
import { CompanyInfoReportsPath } from './path/SelfService/CompanyInfo/CompanyInfoReportsPath.js';
import { CompanyInfoSettingsPath } from './path/SelfService/CompanyInfo/CompanyInfoSettingsPath';
import { DnsManagementPath } from './path/SelfService/DnsManagement/DnsManagementPath.js';
import { DnsRecordHistoryPath } from './path/SelfService/DnsManagement/DnsRecordHistoryPath.js';
import { DnsRecordsHistoryPath } from './path/SelfService/DnsManagement/DnsRecordsHistoryPath.js';
import { DnsRecordsPath } from './path/SelfService/DnsManagement/DnsRecordsPath.js';
import { OrderConfirmationsPath } from './path/SelfService/MessageCenter/OrderConfirmationsPath';
import { SupportCasesAndMessagesPath } from './path/SelfService/MessageCenter/SupportCasesAndMessagesPath';
import { WarrantyReceiptsPath } from './path/SelfService/MessageCenter/WarrantyReceiptsPath';
import { CustomerOrderPath } from './path/SelfService/OrderHistory/CustomerOrderPath.js';
import { CustomerOrderProductPath } from './path/SelfService/OrderHistory/CustomerOrderProductPath.js';
import { CustomerOrderRedeemRequestPath } from './path/SelfService/OrderHistory/CustomerOrderRedeemRequestPath.js';
import { CustomerOrderRedeemRequestsPath } from './path/SelfService/OrderHistory/CustomerOrderRedeemRequestsPath.js';
import { CustomerOrdersPath } from './path/SelfService/OrderHistory/CustomerOrdersPath.js';
import { BroadbandSubActivateSimPath } from './path/SelfService/ProductsAndServices/BroadbandSubActivateSimPath.js';
import { BroadbandSubAddOnPath } from './path/SelfService/ProductsAndServices/BroadbandSubAddOnPath';
import { BroadbandSubNewM2MPath } from './path/SelfService/ProductsAndServices/BroadbandSubNewM2MPath.js';
import { BroadbandSubNewPath } from './path/SelfService/ProductsAndServices/BroadbandSubNewPath.js';
import { BroadbandSubOrderSimPath } from './path/SelfService/ProductsAndServices/BroadbandSubOrderSimPath.js';
import { BroadbandSubTechnicalDetailsPath } from './path/SelfService/ProductsAndServices/BroadbandSubTechnicalDetailsPath.js';
import { BroadbandSubUpdateM2MPath } from './path/SelfService/ProductsAndServices/BroadbandSubUpdateM2MPath.js';
import { BroadbandSubUpdatePath } from './path/SelfService/ProductsAndServices/BroadbandSubUpdatePath.js';
import { BroadbandSubsPath } from './path/SelfService/ProductsAndServices/BroadbandSubsPath.js';
import { ContractsPath } from './path/SelfService/ProductsAndServices/ContractsPath.js';
import { DeviceAddOnPath } from './path/SelfService/ProductsAndServices/DeviceAddOnPath';
import { DeviceAddonsPath } from './path/SelfService/ProductsAndServices/DeviceAddonsPath';
import { DeviceBasicDetailsPath } from './path/SelfService/ProductsAndServices/DeviceBasicDetailsPath';
import { DeviceBillingAccountUserInformationPath } from './path/SelfService/ProductsAndServices/DeviceBillingAccountUserInformationPath';
import { DeviceRedemptionPath } from './path/SelfService/ProductsAndServices/DeviceRedemptionPath.js';
import { DeviceSupportRequestPath as NoeDeviceSupportRequestPath } from './path/SelfService/ProductsAndServices/DeviceSupportRequestPath.js';
import { DevicesPath as NoeDevicesPath } from './path/SelfService/ProductsAndServices/DevicesPath.js';
import { LicenseManagementPath } from './path/SelfService/ProductsAndServices/LicenseManagementPath.js';
import { MobiilivarmennePath } from './path/SelfService/ProductsAndServices/MobiilivarmennePath.js';
import { MobileBillingAccountUserInformationPath } from './path/SelfService/ProductsAndServices/MobileBillingAccountUserInformationPath';
import { MobileIdDetailsPath } from './path/SelfService/ProductsAndServices/MobileIdDetailsPath.js';
import { MobileIdNewPath } from './path/SelfService/ProductsAndServices/MobileIdNewPath.js';
import { MobileSubActivateSimPath } from './path/SelfService/ProductsAndServices/MobileSubActivateSimPath.js';
import { MobileSubAddOnPath } from './path/SelfService/ProductsAndServices/MobileSubAddOnPath';
import { MobileSubAddonsPath } from './path/SelfService/ProductsAndServices/MobileSubAddonsPath.js';
import { MobileSubAttachRingPath } from './path/SelfService/ProductsAndServices/MobileSubAttachRingPath.js';
import { MobileSubAttachVakioPath } from './path/SelfService/ProductsAndServices/MobileSubAttachVakioPath.js';
import { MobileSubBarringServicesPath } from './path/SelfService/ProductsAndServices/MobileSubBarringServicesPath.js';
import { MobileSubBasicDetailsPath } from './path/SelfService/ProductsAndServices/MobileSubBasicDetailsPath.js';
import { MobileSubNewPath } from './path/SelfService/ProductsAndServices/MobileSubNewPath.js';
import { MobileSubOrderSimPath } from './path/SelfService/ProductsAndServices/MobileSubOrderSimPath.js';
import { MobileSubSimCardPath } from './path/SelfService/ProductsAndServices/MobileSubSimCardPath.js';
import { MobileSubUpdateConfirmationPath } from './path/SelfService/ProductsAndServices/MobileSubUpdateConfirmationPath.js';
import { MobileSubUpdatePath } from './path/SelfService/ProductsAndServices/MobileSubUpdatePath.js';
import { MobileSubsPath } from './path/SelfService/ProductsAndServices/MobileSubsPath.js';
import { OrderMobileVoiceAddOnsPath } from './path/SelfService/ProductsAndServices/OrderMobileVoiceAddOnsPath.js';
import { ServiceBillingAccountUserInformationPath } from './path/SelfService/ProductsAndServices/ServiceBillingAccountUserInformationPath.js';
import { ServicePath } from './path/SelfService/ProductsAndServices/ServicePath.js';
import { ServicesPath } from './path/SelfService/ProductsAndServices/ServicesPath.js';
import { VoiceSubNumberSettingsPath } from './path/SelfService/ProductsAndServices/VoiceSubNumberSettingsPath.js';
import { VoiceSubRingSettingsPath } from './path/SelfService/ProductsAndServices/VoiceSubRingSettingsPath.js';
import { VoiceSubTimeSettingsPath } from './path/SelfService/ProductsAndServices/VoiceSubTimeSettingsPath.js';
import { VoiceSubVakioSettingsPath } from './path/SelfService/ProductsAndServices/VoiceSubVakioSettingsPath.js';
import { SelfServiceHomePath } from './path/SelfService/SelfServiceHomePath.js';
import { ShoppingBasketPath } from './path/ShoppingBasketPath.js';
import { ShoppingCartPath } from './path/ShoppingCartPath.js';
import { SupportCasePublicPath } from './path/SupportCasePublicPath.js';
import { TrainingEnrollmentPath } from './path/TrainingEnrollmentPath.js';
import { VaihtoetuPath } from './path/VaihtoetuPath';
import { AuthErrorBoundary, LeafErrorBoundary, RootErrorBoundary, Spinner } from './siteUtils.js';

import '../../common/style/component-library-overrides.scss';
import '../../common/style/global-styles.scss';
import '../../common/style/pattern-library-overrides.scss';
import './SitePath.scss';

// Whenever making changes to this file (that changes the paths/routes in RRD) you need to run `yarn generate-routes`.

// Navigation only depends on the locale, so refetch it on locale changes and on explicit revalidations only.
const navigationLocale = ({ pathname }: URL) => resolveLocaleFromPath(pathname, getLocale())?.content ?? getLocale();
const navigationShouldRevalidate = ({ currentUrl, nextUrl }: { currentUrl: URL; nextUrl: URL }) =>
  currentUrl.href === nextUrl.href || navigationLocale(currentUrl) !== navigationLocale(nextUrl);

const SitePath = () => (
  <Route
    element={<SiteFn />}
    id="site-navigation"
    loader={fetchNavigationLoader}
    shouldRevalidate={navigationShouldRevalidate}
    hydrateFallbackElement={<Spinner />}
    errorElement={<RootErrorBoundary />}
  >
    <Route element={<PrivateAuthenticationLayout />} errorElement={<RootErrorBoundary />}>
      <Route
        path={paths.SELECT_SIGNATORY_RIGHTS}
        element={<SelectCompanySignatoryRightPath />}
        errorElement={<RootErrorBoundary />}
      />
      <Route path="/omaelisa/multisim" element={<AuxiliaryEsimOrderPath />}>
        <Route path={paths.WILDCARD} element={<AuxiliaryEsimOrderPath />} />
      </Route>
      <Route path="/omaelisa/palauta-salasana" element={<ForgotPasswordPath />} />
      <Route path="/omaelisa" element={<OmaElisaNavigationLayout />} errorElement={<RootErrorBoundary />}>
        <Route errorElement={<RootErrorBoundary />}>
          <Route element={<OmaElisaProtectedRoutesLayout />} errorElement={<AuthErrorBoundary isEoe={false} />}>
            {/* Catch-all for unmatched routes under NOE */}
            <Route path={paths.WILDCARD} errorElement={<LeafErrorBoundary />} loader={notFoundLoader} />

            {/* Home path */}
            <Route index element={<SelfServiceHomePath />} />

            {/* Company Info paths */}
            <Route path="yritystiedot">
              <Route index element={<CompanyInfoHomePath />} loader={companyInfoHomeLoader} />
              <Route path="asetukset" id="settingsRoot" loader={mfaDetailsLoader}>
                <Route index element={<CompanyInfoSettingsPath />} loader={companyInfoHomeLoader} />
                <Route path="mfa-self-service" element={<MfaSelfService />} />
              </Route>
              <Route path="käyttäjät">
                <Route index element={<CompanyInfoContactsPath />} loader={contactsLoader} />
                <Route path=":contactMasterId">
                  <Route index element={<CompanyInfoContactPath />} loader={contactLoader} />
                  <Route path="siirrä-käyttäjä" element={<CompanyInfoMoveContactPath />} loader={moveContactLoader} />
                </Route>
                <Route path="lisää-uusi-käyttäjä" element={<CompanyInfoNewContactPath />} />
              </Route>
              <Route
                path="kutsut"
                element={isElisaIdV3Enabled() ? <CompanyInfoInvitationsPath /> : <CompanyInfoInvitesPath />}
                loader={isElisaIdV3Enabled() ? invitationsLoader : companyInvitesLoader}
                shouldRevalidate={({ currentUrl, nextUrl }) => currentUrl.href === nextUrl.href} // We always fetch all data, thus only need to reload when `revalidate()` is called, thus no change in href.
              />
              <Route path="laitelistat">
                <Route element={<CompanyInfoCatalogsOutlet />}>
                  <Route index element={<CompanyInfoCatalogsPath />} loader={virtualCatalogsLoader} />
                  <Route
                    path={`:${catalogCode}`}
                    element={<CompanyInfoCatalogPath />}
                    loader={virtualCatalogDetailsLoader}
                  />
                  <Route
                    path="uusi-laitelista"
                    element={<CompanyInfoCatalogListNewPath />}
                    loader={catalogConfigurationsLoader}
                  />
                  <Route
                    path="laitelistan-muokkaus"
                    element={<CompanyInfoCatalogListEditPath />}
                    loader={catalogConfigurationsLoader}
                  />
                  <Route
                    path="laitelistan-tuotteet"
                    element={<CompanyInfoCatalogListProductsPath />}
                    loader={catalogProductsLoader}
                  />
                  <Route
                    path="laitelistan-tiedot"
                    element={<CompanyInfoCatalogListInfoPath />}
                    loader={catalogSummaryLoader}
                  />
                </Route>
              </Route>
              <Route path="raportit" element={<CompanyInfoReportsPath />} loader={reportsLoader} />
              <Route path="omat-tiedot" element={<CompanyInfoMyAccountPath />} />
            </Route>

            {/* Billing paths */}
            <Route path="laskutussopimukset">
              <Route index element={<BillingAccountsPath />} loader={billingAccountListLoader} />
              <Route
                path="luo-uusi-laskutussopimus"
                element={<BillingAccountCreateNewPath />}
                loader={createNewBillingAccountLoader}
              />
              <Route
                path={`:${billingAccountId}`}
                element={<BillingAccountLayout />}
                id="baRoot"
                loader={billingAccountLayoutLoader}
                shouldRevalidate={({ currentUrl, nextUrl }) => currentUrl.pathname !== nextUrl.pathname}
              >
                <Route index element={<BillingAccountPath />} loader={billingAccountLoader} />
                <Route
                  path="tuotteet"
                  element={<BillingAccountSubscriptionsPath />}
                  loader={billingAccountSubscriptionsLoader}
                />
                <Route path="laskut" element={<BillingAccountInvoicesPath />} loader={billingAccountInvoicesLoader} />
                <Route
                  path="kirjeet"
                  element={<BillingAccountInvoiceDocumentsPath />}
                  loader={billingAccountInvoiceDocumentsLoader}
                />
              </Route>
            </Route>
            <Route path="laskut">
              <Route index element={<InvoicesPath />} loader={invoicesLoader} />
              <Route path={`:${invoiceId}`} element={<InvoicePath />} loader={invoiceLoader} />
            </Route>
            <Route path="laskutukseen-liittyvät-kirjeet" element={<InvoiceDocumentsPath />} loader={documentsLoader} />

            {/* Order History paths */}
            <Route path="tilaushistoria">
              <Route index element={<Navigate replace to={paths.CUSTOMER_ORDERS} />} />
              <Route path="tilaukset">
                <Route index element={<CustomerOrdersPath />} loader={customerOrdersLoader} />
                <Route path={`:${orderId}`}>
                  <Route index element={<CustomerOrderPath />} loader={customerOrderLoader} />
                  <Route path={`:${deliveryId}`} element={<CustomerOrderProductPath />} loader={deliveryOrderLoader} />
                </Route>
              </Route>
              <Route path="lunastuspyynnot">
                <Route index element={<CustomerOrderRedeemRequestsPath />} loader={subscriptionActionsLoader} />
                <Route
                  path={`:${requestId}`}
                  element={<CustomerOrderRedeemRequestPath />}
                  loader={subscriptionActionLoader}
                />
              </Route>
            </Route>

            {/* Products and Services paths */}
            <Route path="tuotteeni-ja-palveluni">
              <Route index element={<Navigate replace to={paths.PS_MOBILE_SUBSCRIPTIONS} />} />
              <Route path="puhelinliittymät">
                <Route index element={<MobileSubsPath />} loader={voiceSubscriptionsLoader} />
                <Route
                  path="tilaa-lisäpalveluita"
                  element={<OrderMobileVoiceAddOnsPath />}
                  loader={voiceSubscriptionsLoader}
                />
                <Route
                  path="uusi-liittymä"
                  element={<MobileSubNewPath />}
                  loader={orderSubscriptionCommonLoader}
                  id="newVoiceSubCommon"
                />
                <Route path={`:${subscriptionId}`} id="voiceSub" loader={voiceSubscriptionLoader}>
                  <Route index element={<MobileSubBasicDetailsPath />} />
                  <Route path="laskutus-ja-käyttäjätiedot" element={<MobileBillingAccountUserInformationPath />} />
                  <Route path="estopalvelut" element={<MobileSubBarringServicesPath />} />
                  <Route path="aika-asetukset" element={<VoiceSubTimeSettingsPath />} />
                  <Route path="numeroasetukset" element={<VoiceSubNumberSettingsPath />} />
                  <Route path="lisäpalvelut" element={<MobileSubAddonsPath />} />
                  <Route path="sim-kortti" element={<MobileSubSimCardPath />} />
                  <Route path="ring" element={<VoiceSubRingSettingsPath />} />
                  <Route path="vakio" element={<VoiceSubVakioSettingsPath />} />
                  <Route
                    path="aktivoi-sim-kortti"
                    element={<MobileSubActivateSimPath />}
                    loader={mobileSubPostChangeRequestLoader}
                  />
                  <Route path="liitä-ring" element={<MobileSubAttachRingPath />} loader={mobileSubAttachRingLoader} />
                  <Route
                    path="liitä-vakio"
                    element={<MobileSubAttachVakioPath />}
                    loader={mobileSubAttachVakioLoader}
                  />
                  <Route path="päivitä-liittymä" element={<MobileSubUpdatePath />} loader={mobileSubUpdateLoader} />
                  <Route
                    path="päivitä-liittymä/vahvista"
                    element={<MobileSubUpdateConfirmationPath />}
                    loader={mobileSubUpdateConfirmationLoader}
                  />
                  <Route
                    path="tilaa-sim"
                    element={<MobileSubOrderSimPath />}
                    loader={mobileSubPostChangeRequestLoader}
                  />
                  <Route path={`:${addonId}`} element={<MobileSubAddOnPath />} loader={mobileSubAddOnLoader} />
                </Route>
              </Route>
              <Route path="nettiliittymät">
                <Route index element={<BroadbandSubsPath />} loader={broadbandSubscriptionsLoader} />
                <Route
                  path="uusi-mobiililaajakaista"
                  element={<BroadbandSubNewPath />}
                  loader={orderMobileBBCommonLoader}
                  id="newMobileBBCommon"
                />
                <Route
                  path="uusi-laitenetti"
                  element={<BroadbandSubNewM2MPath />}
                  loader={orderSubscriptionCommonLoader}
                  id="newM2MCommon"
                />
                <Route path={`:${subscriptionId}`} id="broadbandSub" loader={broadbandSubscriptionLoader}>
                  <Route index element={<MobileSubBasicDetailsPath />} />
                  <Route path="laskutus-ja-käyttäjätiedot" element={<MobileBillingAccountUserInformationPath />} />
                  <Route path="estopalvelut" element={<MobileSubBarringServicesPath />} />
                  <Route path="lisäpalvelut" element={<MobileSubAddonsPath />} />
                  <Route path="sim-kortti" element={<MobileSubSimCardPath />} />
                  <Route path="tekniset-tiedot" element={<BroadbandSubTechnicalDetailsPath />} />
                  <Route
                    path="aktivoi-sim-kortti"
                    element={<BroadbandSubActivateSimPath />}
                    loader={broadbandSubPostChangeRequestLoader}
                  />
                  <Route
                    path="tilaa-sim"
                    element={<BroadbandSubOrderSimPath />}
                    loader={broadbandSubPostChangeRequestLoader}
                  />
                  <Route
                    path="päivitä-mobiililaajakaista"
                    element={<BroadbandSubUpdatePath />}
                    loader={broadbandSubUpdateLoader}
                  />
                  <Route
                    path="päivitä-mobiililaajakaista/vahvista"
                    element={<MobileSubUpdateConfirmationPath />}
                    loader={mobileSubUpdateConfirmationLoader}
                  />
                  <Route
                    path="päivitä-laitenetti"
                    element={<BroadbandSubUpdateM2MPath />}
                    loader={broadbandSubUpdateM2MLoader}
                  />
                  <Route
                    path="päivitä-laitenetti/vahvista"
                    element={<MobileSubUpdateConfirmationPath />}
                    loader={mobileSubUpdateConfirmationLoader}
                  />
                  <Route path={`:${addonId}`} element={<BroadbandSubAddOnPath />} loader={broadbandSubAddOnLoader} />
                </Route>
              </Route>
              <Route path="laitteet">
                <Route index element={<NoeDevicesPath />} loader={deviceSubscriptionsLoader} />
                <Route path={`:${subscriptionId}`} id="deviceSub" loader={deviceSubscriptionLoader}>
                  <Route index element={<DeviceBasicDetailsPath />} />
                  <Route path="laskutus-ja-käyttäjätiedot" element={<DeviceBillingAccountUserInformationPath />} />
                  <Route path="lisäpalvelut" element={<DeviceAddonsPath />} />
                  <Route
                    path="tee-tukipyynto"
                    element={<NoeDeviceSupportRequestPath />}
                    loader={deviceSubPostChangeRequestLoader}
                  />
                  <Route
                    path="laitteen-lunastaminen"
                    element={<DeviceRedemptionPath />}
                    loader={deviceSubPostChangeRequestLoader}
                    shouldRevalidate={({ currentUrl, nextUrl }) => {
                      return nextUrl.pathname !== currentUrl.pathname;
                    }}
                  />
                  <Route path={`:${addonId}`} element={<DeviceAddOnPath />} loader={deviceAddOnLoader} />
                </Route>
              </Route>
              <Route path="palvelut">
                <Route index element={<ServicesPath />} loader={serviceSubscriptionsLoader} />
                <Route path={`:${subscriptionId}`} id="serviceSub" loader={serviceSubscriptionLoader}>
                  <Route index element={<ServicePath />} />
                  <Route path="laskutus-ja-käyttäjätiedot" element={<ServiceBillingAccountUserInformationPath />} />
                </Route>
              </Route>
              <Route path="mobiilivarmenne">
                <Route index element={<MobiilivarmennePath />} />
              </Route>
              <Route path="lisenssit" element={<LicenseManagementPath />} />
              <Route path="sopimukset">
                <Route index element={<ContractsPath />} loader={mobileIdContractsLoader} />
                <Route path="uusi-mobiilivarmenne" element={<MobileIdNewPath />} loader={newMobileIdContractLoader} />
                <Route
                  path={`:${contractDisplayId}`}
                  element={<MobileIdDetailsPath />}
                  loader={mobileIdContractLoader}
                />
              </Route>
              <Route path="elisa-dns">
                <Route index element={<DnsManagementPath />} loader={dnsSubscriptionsLoader} />
                <Route path={`:${subscriptionId}`}>
                  <Route index element={<DnsRecordsPath />} loader={getDnsRecords} />
                  <Route path="muutoshistoria">
                    <Route index element={<DnsRecordsHistoryPath />} loader={dnsRecordsHistoryLoader} />
                    <Route path={`:${historyId}`} element={<DnsRecordHistoryPath />} loader={dnsRecordHistoryLoader} />
                  </Route>
                </Route>
              </Route>
            </Route>

            {/* Message center paths */}
            <Route path="tuki">
              <Route index element={<Navigate replace to={paths.MESSAGE_CENTER_SUPPORT_CASES} />} />
              <Route path="tukipyynnöt">
                <Route index element={<SupportCasesAndMessagesPath />} loader={supportCasesLoader} />
                <Route path="luo-uusi" element={<NewSupportCaseAuthenticatedPath />} loader={supportCaseFormLoader} />
                <Route path={`:${caseDisplayId}`} element={<SupportCasePath />} loader={supportCaseLoader} />
              </Route>
              <Route path="tilausvahvistukset" element={<OrderConfirmationsPath />} loader={orderConfirmationsLoader} />
              <Route path="takuukuitit" element={<WarrantyReceiptsPath />} loader={warrantyReceiptsLoader} />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="/punchout" element={<PunchoutPath />}>
        {/* Catch-all for unmatched routes under Punchout */}
        <Route path={paths.WILDCARD} errorElement={<RootErrorBoundary />} loader={notFoundLoader} />

        <Route index element={<Navigate replace to={paths.PUNCHOUT_STORE} />} />
        <Route
          path="kassa"
          element={<PunchoutCheckoutPath />}
          loader={punchoutCheckoutLoader}
          id="checkoutRootPunchout"
          errorElement={<RootErrorBoundary />}
        />
        <Route
          path="ostoskori"
          element={<ShoppingCartPath header={<PunchoutHeader />} />}
          loader={onlineModelsForBasketItemsLoader}
          errorElement={<RootErrorBoundary />}
        />
        <Route path="kauppa" element={<PunchoutNavigationLayout />}>
          <Route index element={<PunchoutStorePath />} />
          <Route path="puhelimet" errorElement={<RootErrorBoundary />}>
            <Route
              index
              element={<PunchoutStoreProducts category={categories.PHONE} />}
              loader={categoryOnlineModelHeadersLoader(categories.PHONE.code)}
            />
            <Route
              path={`:${deviceName}`}
              element={<PunchoutStoreProduct category={categories.PHONE} />}
              loader={fetchProductPageLoader}
            />
          </Route>
          <Route path="tabletit" errorElement={<RootErrorBoundary />}>
            <Route
              index
              element={<PunchoutStoreProducts category={categories.TABLET} />}
              loader={categoryOnlineModelHeadersLoader(categories.TABLET.code)}
            />
            <Route
              path={`:${deviceName}`}
              element={<PunchoutStoreProduct category={categories.TABLET} />}
              loader={fetchProductPageLoader}
            />
          </Route>
          <Route path="tietokoneet" errorElement={<RootErrorBoundary />}>
            <Route
              index
              element={<PunchoutStoreProducts category={categories.COMPUTER} />}
              loader={categoryOnlineModelHeadersLoader(categories.COMPUTER.code)}
            />
            <Route
              path={`:${deviceName}`}
              element={<PunchoutStoreProduct category={categories.COMPUTER} />}
              loader={fetchProductPageLoader}
            />
          </Route>
          <Route path="verkkolaitteet" errorElement={<RootErrorBoundary />}>
            <Route
              index
              element={<PunchoutStoreProducts category={categories.NETWORK} />}
              loader={categoryOnlineModelHeadersLoader(categories.NETWORK.code)}
            />
            <Route
              path={`:${deviceName}`}
              element={<PunchoutStoreProduct category={categories.NETWORK} />}
              loader={fetchProductPageLoader}
            />
          </Route>
          <Route path="lisatarvikkeet" errorElement={<RootErrorBoundary />}>
            <Route
              index
              element={<PunchoutStoreProducts category={categories.ACCESSORY} />}
              loader={categoryOnlineModelHeadersLoader(categories.ACCESSORY.code)}
            />
            <Route
              path={`:${deviceName}`}
              element={<PunchoutStoreProduct category={categories.ACCESSORY} />}
              loader={fetchProductPageLoader}
            />
          </Route>
        </Route>
      </Route>
      <Route element={<ShoppingBasketLayout />}>
        <Route path="/ostoskori" element={<ShoppingBasketPath />} />
      </Route>
      <Route
        path={paths.INVITATION_LANDING}
        element={<InvitationLandingPath />}
        loader={invitationLandingLoader}
        errorElement={<RootErrorBoundary />}
      />
      <Route element={<PlaceOrderLayout />}>
        <Route path="/kassa/maksu" element={<PlaceOrderCardPayment />} />
        <Route path="/kassa/kiitos" element={<PlaceOrderThankYou />} />
      </Route>
      <Route element={<PrivateAuthenticationLayout />} errorElement={<RootErrorBoundary />}>
        <Route element={<PlaceOrderLayout />}>
          <Route path="/kassa" element={<PlaceOrderPath />} loader={placeOrderLoader} />
        </Route>
      </Route>
      <Route
        path={`/ilmo/:${trainingName}`}
        element={<TrainingEnrollmentPath />}
        loader={args => fetchTrainings(args.params.trainingName!)}
      />
      <Route path="/tunnistaudu" element={<ExtAuthLayout />}>
        {/* TODO vahvistettu-numeronsiirto claims to use :token, but it doesn't. Why? */}
        <Route path={`vahvistettu-numeronsiirto/:${token}`} element={<AuthorizedNumberPortingPath />} />
        <Route path={`vahvista-numeronsiirto/:${token}`} element={<AuthorizeNumberPortingPath />} />
        <Route path="paluu" element={<AuthenticationReturnPath />} />
        <Route index path={paths.WILDCARD} element={<Navigate replace to={paths.NOT_FOUND_404} />} />
      </Route>
      <Route path="/haku" element={<SearchPublicPagesPath />} />

      <Route path="/työntekijä" errorElement={<AuthErrorBoundary isEoe={true} />}>
        <Route path="kassa" element={<EoeCheckout />} loader={checkoutRootLoader} id="checkoutRootEmployee">
          <Route path="kiitos" element={<EoeCheckoutThankYouOrError />} />
          <Route index element={<EoeCheckoutSteps rootLoaderId="checkoutRootEmployee" />} />
        </Route>
        <Route path="vahvistus" element={<EoeSelfRegistrationPath />} />
        <Route path="kutsu-vahvistus" element={<EoeInvitationAcceptancePath />} />
        {/* Employee Email Verification - needs to work for unauthenticated users as well */}
        <Route path={`emailvahvistus/:${token}`} element={<EoeEmailVerificationPath />} />
        <Route element={<EoeOutlet />} errorElement={<LeafErrorBoundary />}>
          <Route errorElement={<LeafErrorBoundary />}>
            {/* Catch-all for unmatched routes under EOE */}
            <Route path={paths.WILDCARD} errorElement={<LeafErrorBoundary />} loader={notFoundLoader} />

            {/* Employee Home */}
            <Route index element={<EoeHomePath />} />

            {/* Employee Devices and Subscriptions */}
            <Route path="puhelinliittymät">
              <Route index element={<Navigate replace to={paths.EMPLOYEE_SUBSCRIPTIONS} />} />
              <Route path={`:${subscriptionId}`}>
                <Route index element={<EoeVoiceSubPath />} />
                <Route path="tilaa-sim" element={<EoeVoiceSubOrderSimPath />} />
                <Route path="aktivoi-sim-kortti" element={<EoeVoiceSubActivateSimPath />} />
              </Route>
              <Route path="lisää">
                <Route index element={<EoeSubAddPath />} />
                <Route path="vahvistuskoodi" element={<EoeOneTimePasswordPath />} />
              </Route>
            </Route>

            <Route path="nettiliittymät">
              <Route index element={<Navigate replace to={paths.EMPLOYEE_SUBSCRIPTIONS} />} />
              <Route path={`:${subscriptionId}`} element={<EoeBroadbandSubPath />} />
              <Route path="lisää">
                <Route index element={<EoeSubAddPath />} />
                <Route path="vahvistuskoodi" index element={<EoeOneTimePasswordPath />} />
              </Route>
            </Route>

            <Route path="liittymat" element={<EoeSubsPath />} />
            <Route path="ostoskori" element={<ShoppingCartPath />} loader={onlineModelsForBasketItemsLoader} />

            <Route path="laitteet">
              <Route index element={<EoeDevicesPath />} />
              <Route path={`:${subscriptionId}`}>
                <Route index element={<EoeDevicePath />} loader={eoeDevicePathLoader} />
                <Route path="tee-tukipyynto" element={<EoeDeviceSupportRequestPath />} />
              </Route>
              <Route path="lisää">
                <Route index element={<EoeDeviceAddPath />} />
                <Route path="vahvistuskoodi" element={<EoeOneTimePasswordPath />} />
              </Route>
            </Route>

            {/* Employee Device Store */}
            <Route path="kauppa">
              <Route index element={<EoeStore />} />
              <Route path="puhelimet">
                <Route
                  index
                  element={<EoeStoreProducts category={categories.PHONE} />}
                  loader={categoryOnlineModelHeadersLoader(categories.PHONE.code)}
                />
                <Route
                  path={`:${deviceName}`}
                  element={<EoeStoreProduct category={categories.PHONE} />}
                  loader={fetchProductPageLoader}
                />
              </Route>

              <Route path="tabletit">
                <Route
                  index
                  element={<EoeStoreProducts category={categories.TABLET} />}
                  loader={categoryOnlineModelHeadersLoader(categories.TABLET.code)}
                />
                <Route
                  path={`:${deviceName}`}
                  element={<EoeStoreProduct category={categories.TABLET} />}
                  loader={fetchProductPageLoader}
                />
              </Route>

              <Route path="tietokoneet">
                <Route
                  index
                  element={<EoeStoreProducts category={categories.COMPUTER} />}
                  loader={categoryOnlineModelHeadersLoader(categories.COMPUTER.code)}
                />
                <Route
                  path={`:${deviceName}`}
                  element={<EoeStoreProduct category={categories.COMPUTER} />}
                  loader={fetchProductPageLoader}
                />
              </Route>

              <Route path="verkkolaitteet">
                <Route
                  index
                  element={<EoeStoreProducts category={categories.NETWORK} />}
                  loader={categoryOnlineModelHeadersLoader(categories.NETWORK.code)}
                />
                <Route
                  path={`:${deviceName}`}
                  element={<EoeStoreProduct category={categories.NETWORK} />}
                  loader={fetchProductPageLoader}
                />
              </Route>

              <Route path="lisatarvikkeet">
                <Route
                  index
                  element={<EoeStoreProducts category={categories.ACCESSORY} />}
                  loader={categoryOnlineModelHeadersLoader(categories.ACCESSORY.code)}
                />
                <Route
                  path={`:${deviceName}`}
                  element={<EoeStoreProduct category={categories.ACCESSORY} />}
                  loader={fetchProductPageLoader}
                />
              </Route>
            </Route>

            {/* Employee Orders for approval */}
            <Route path="tilaukset" id="eoe-pending-orders" loader={subscriptionActionsLoader}>
              <Route index element={<Navigate replace to={paths.EMPLOYEE_PENDING_REVIEW_ORDERS} />} />
              <Route path="muutospyynnöt">
                <Route index element={<EoeChangeRequestsPath />} loader={subscriptionActionsLoader} />
                <Route path={`:${requestId}`} element={<EoeChangeRequestPath />} loader={subscriptionActionLoader} />
              </Route>

              <Route path="käsitellyt">
                <Route index element={<EoeFinishedReviewsPath />} loader={customerOrdersLoader} />
                <Route path={`:${orderId}`}>
                  <Route index element={<EoeFinishedReviewPath />} loader={employeeApprovalCustomerOrderLoader} />
                  <Route path={`:${deliveryId}`} element={<EoeFinishedReviewItemPath />} loader={deliveryOrderLoader} />
                </Route>
              </Route>

              <Route path="odottaa">
                <Route index element={<EoePendingReviewsPath />} loader={customerOrdersLoader} />
                <Route path={`:${orderId}`}>
                  <Route index element={<EoePendingReviewPath />} loader={employeeApprovalCustomerOrderLoader} />
                  <Route path={`:${deliveryId}`} element={<EoePendingReviewItemPath />} loader={deliveryOrderLoader} />
                </Route>
              </Route>
            </Route>

            {/* Employee Own Information */}
            <Route path="omat-tiedot" element={<EoeOwnInfoPath />} />

            {/* Employee Omalasku */}
            <Route
              path="omalasku"
              id="employeeVoiceSubscriptions"
              element={<EoeOmalasku />}
              errorElement={<LeafErrorBoundary />}
            >
              <Route index element={<EoeOmalaskuSelfService />} />
              <Route path={`:${msisdn}`} element={<EoeOmalaskuOrderBarrings />} />
            </Route>

            {/* Employee Own Orders */}
            <Route path="omat">
              <Route index element={<Navigate replace to={paths.EMPLOYEE_OWN_ORDERS} />} />
              <Route path="tilaukset">
                <Route index element={<EoeOwnOrdersPath />} loader={userCustomerOrdersLoader} />
                <Route path={`:${orderId}`} id="eoe-own-orders" loader={userCustomerOrderLoader}>
                  <Route index element={<EoeOwnOrderPath />} />
                  <Route path={`:${deliveryId}`} element={<EoeOwnOrderItemPath />} />
                </Route>
              </Route>

              <Route path="muutospyynnöt">
                <Route index element={<EoeOwnChangeRequestsPath />} loader={subscriptionActionsLoader} />
                <Route path={`:${requestId}`} element={<EoeOwnChangeRequestPath />} loader={subscriptionActionLoader} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>
    <Route path={paths.ERROR} element={<PublicPathLayout />} errorElement={<RootErrorBoundary />}>
      <Route element={<PublicAuthenticationLayout />}>
        <Route element={<PublicHeaderLayout />}>
          <Route index element={<ErrorPath />} />
        </Route>
      </Route>
    </Route>
    <Route path="ideat" element={<PublicHeaderCmsLayout />} errorElement={<RootErrorBoundary />}>
      <Route index id="ideat-landing-root" loader={fetchIdeatLandingLoader} element={<IdeatLandingPath />} />
      <Route
        path="page/:pageNumber"
        id="ideat-landing-page"
        loader={fetchIdeatLandingLoader}
        element={<IdeatLandingPath />}
      />
      <Route
        path="kategoriat/:ideatCategorySlug"
        id="ideat-category-root"
        loader={fetchIdeatCategoryWithMetaLoader}
        element={<IdeatCategoryPath />}
      />
      <Route
        path="kategoriat/:ideatCategorySlug/page/:pageNumber"
        id="ideat-category-page"
        loader={fetchIdeatCategoryWithMetaLoader}
        element={<IdeatCategoryPath />}
      />
      <Route
        path="avainsanat/:ideatTagSlug"
        id="ideat-tag-root"
        loader={fetchIdeatTagWithMetaLoader}
        element={<IdeatTagPath />}
      />
      <Route
        path="avainsanat/:ideatTagSlug/page/:pageNumber"
        id="ideat-tag-page"
        loader={fetchIdeatTagWithMetaLoader}
        element={<IdeatTagPath />}
      />
      <Route
        path="kaikki-avainsanat"
        id="ideat-keywords-root"
        loader={fetchIdeatKeywordsLoader}
        element={<IdeatKeywordsPath />}
      />
      <Route
        path=":ideatArticleSlug"
        id="ideat-article-root"
        loader={fetchIdeatArticleLoader}
        element={<IdeatArticlePath />}
      />
    </Route>
    <Route path="openform" element={<OpenFormLayout />}>
      {/* Catch-all for unmatched routes under OpenForm */}
      <Route index path={paths.WILDCARD} errorElement={<RootErrorBoundary />} loader={notFoundLoader} />
      <Route path="list/draft" element={<OpenFormListLayout />} />
      <Route path="list/form" element={<OpenFormListLayout />} />
      <Route path={`preview/:${formId}/:${page}?`} element={<OpenFormViewLayout />} />
      <Route path={`view/:${formId}/:${page}?`} element={<OpenFormViewLayout />} />
      <Route path={`view/:${formId}/completed`} element={<OpenFormThankYou />} />
    </Route>
    <Route id="navigation-path" loader={fetchNavigationGroupsLoader}>
      <Route
        path={paths.WILDCARD}
        element={<PublicPathLayout />}
        errorElement={<RootErrorBoundary />}
        id="public-path-root"
        loader={fetchPublicPageLoader}
      >
        <Route element={<PublicAuthenticationLayout />}>
          <Route element={<PublicHeaderCmsLayout />}>
            <Route path="ohjeet" element={<InstructionsPath />} />
            <Route path={paths.WILDCARD} element={<PublicPath />} />
          </Route>
          <Route element={<PublicHeaderLayout />}>
            <Route path="laitenetti" element={<PublicM2MSubscriptions />} loader={publicM2MBroadbandPageLoader} />
            <Route path="mobiililaajakaista" element={<AnonymousMobileBroadbandPath />} />
            <Route path="rekisteroidy" element={<RegistrationPath />} />
            <Route path="yritysliittymat" element={<AnonymousMobileVoicePath />} />
            <Route path="vaihtoetu" element={<VaihtoetuPath />} loader={vaihtoetuItemGroupsLoader} />
          </Route>
          <Route
            path="netin-saatavuus"
            id="fixedBBRoot"
            element={<AnonymousFixedBroadbandOrderPath />}
            loader={fixedBBRootLoader}
          >
            <Route index element={<FixedBroadbandAvailabilityPath />} />
            <Route path="valitse-nettiyhteys" element={<FixedBroadbandOffersPath />} />
            <Route path="tilaa-nettiyhteys" element={<FixedBroadbandCheckoutPath />} loader={fixedBBCheckoutLoader} />
            <Route path="kiitos-tilauksesta" element={<FixedBroadbandThankyouPath />} />
          </Route>
          <Route path="ota-yhteytta" element={<PublicHeaderLayout />}>
            <Route index element={<SupportCasePublicPath />} />
            <Route path="luo-uusi-viesti" element={<NewSupportCasePublic />} loader={supportCaseFormPublicLoader} />
            <Route path="kiitos-viestista" element={<NewSupportCasePublicThankYou />} />
          </Route>
        </Route>
      </Route>
    </Route>
  </Route>
);

export const createSiteRoutes = () => createRoutesFromElements(SitePath());
export const siteRoutes = createSiteRoutes();
